import React, { useCallback, useEffect, useRef, useState } from 'react';

export type ScrollAxis = 'horizontal' | 'vertical';

type ThumbState = {
    offset: number;
    size: number;
    visible: boolean;
};

const HIDDEN_THUMB: ThumbState = { offset: 0, size: 0, visible: false };

const AXIS_CONFIG = {
    horizontal: {
        scrollPos: 'scrollLeft',
        scrollSize: 'scrollWidth',
        clientSize: 'clientWidth',
        clientCoord: 'clientX',
    },
    vertical: {
        scrollPos: 'scrollTop',
        scrollSize: 'scrollHeight',
        clientSize: 'clientHeight',
        clientCoord: 'clientY',
    },
} as const;

const useScrollbar = (axis: ScrollAxis = 'horizontal') => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = useState<ThumbState>(HIDDEN_THUMB);

    const updateThumb = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;

        const { scrollPos, scrollSize, clientSize } = AXIS_CONFIG[axis];
        const pos = el[scrollPos];
        const total = el[scrollSize];
        const client = el[clientSize];

        if (total <= client) {
            setThumb((prev) => (prev.visible ? HIDDEN_THUMB : prev));
            return;
        }

        const size = (client / total) * client;
        const offset = (pos / (total - client)) * (client - size);
        setThumb({ offset, size, visible: true });
    }, [axis]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        updateThumb();
        el.addEventListener('scroll', updateThumb);

        // Observe both the container (clientSize) and its content child
        // (scrollSize) so the thumb tracks resizes and content changes alike.
        const observer = new ResizeObserver(updateThumb);
        observer.observe(el);
        if (el.firstElementChild) observer.observe(el.firstElementChild);

        return () => {
            el.removeEventListener('scroll', updateThumb);
            observer.disconnect();
        };
    }, [updateThumb]);

    const onThumbPointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            e.preventDefault();
            const container = scrollRef.current;
            if (!container) return;

            const { scrollPos, scrollSize, clientSize, clientCoord } = AXIS_CONFIG[axis];
            const start = e[clientCoord];
            const startScroll = container[scrollPos];
            const total = container[scrollSize];
            const client = container[clientSize];
            const thumbSize = (client / total) * client;
            const ratio = (total - client) / (client - thumbSize);

            const handlePointerMove = (ev: PointerEvent) => {
                container[scrollPos] = startScroll + (ev[clientCoord] - start) * ratio;
            };
            const handlePointerUp = () => {
                document.removeEventListener('pointermove', handlePointerMove);
                document.removeEventListener('pointerup', handlePointerUp);
            };

            document.addEventListener('pointermove', handlePointerMove);
            document.addEventListener('pointerup', handlePointerUp);
        },
        [axis]
    );

    return { scrollRef, thumb, onThumbPointerDown };
};

export default useScrollbar;
